import { c as _c } from "react/compiler-runtime";
import { cloneElement, isValidElement, useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { X } from "lucide-react";
import { EqualityOperator, InclusionOperator, OperatorType, ValuesSchemaType } from "../schemas";
import { FilterMenuList } from "../FilterMenuList/FilterMenuList";
import { useFilterIndicator } from "./useFilterIndicator";

// Operator enum → translation key. Labels live in the listings filters
// namespace, the de-facto source for operator wording app-wide, so a caller
// can omit `label` and get the default wording for the operator value.
const OPERATOR_LABEL_KEY: Record<string, string> = {
  [EqualityOperator.IS]: "is",
  [EqualityOperator.IS_NOT]: "isNot",
  [InclusionOperator.INCLUDES]: "includes",
  [InclusionOperator.EXCLUDES]: "excludes"
};
type Props = {
  title: string;
  values: ValuesSchemaType;
  activeValues: ValuesSchemaType;
  inactiveValues: ValuesSchemaType;
  onClear?: () => void;
  onChange: (value: string) => void;
  operatorValues: OperatorType;
  /**
   * When set, the value segment opens this content (e.g. a slider/date picker)
   * instead of the checkbox list, and shows `activeLabel` as its text. Only the
   * value input differs — title, operator and clear segments are shared.
   */
  customContent?: ReactNode;
  activeLabel?: string;
};
export function FilterIndicator(t0) {
  const $ = _c(41);
  const {
    title,
    activeValues,
    inactiveValues,
    onClear,
    onChange,
    operatorValues,
    customContent,
    activeLabel
  } = t0;
  const [open, setOpen] = useState(false);
  const tOperators = useTranslations("modules.listings.listingsPage.filters");
  let t1;
  if ($[0] !== activeValues || $[1] !== operatorValues || $[2] !== title) {
    t1 = {
      title,
      activeValues,
      operatorValues
    };
    $[0] = activeValues;
    $[1] = operatorValues;
    $[2] = title;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    displayText,
    activeOperator
  } = useFilterIndicator(t1);
  const isCustom = customContent != null;
  const valueText = activeLabel ?? displayText;
  if (!activeLabel && !activeValues?.length) {
    return null;
  }
  let t2;
  if ($[4] !== activeOperator || $[5] !== tOperators) {
    t2 = activeOperator && (activeOperator.label ?? tOperators(OPERATOR_LABEL_KEY[activeOperator.value]));
    $[4] = activeOperator;
    $[5] = tOperators;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const operatorLabel = t2;
  let t3;
  if ($[7] !== title) {
    t3 = <div className="flex items-center bg-card border-y border-l h-6 px-1.5 rounded-l text-muted-foreground text-xs">{title}</div>;
    $[7] = title;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== activeOperator || $[10] !== operatorLabel || $[11] !== operatorValues || $[12] !== tOperators) {
    t4 = operatorValues.values.length > 0 && activeOperator && (operatorValues.disabled ? <div className="flex items-center bg-card border-y border-l h-6 px-1.5 text-muted-foreground text-xs">{operatorLabel}</div> : <DropdownMenu><DropdownMenuTrigger asChild={true}><div className="flex items-center bg-card border-y border-l h-6 px-1.5 text-muted-foreground text-xs cursor-pointer hover:bg-accent hover:text-foreground">{operatorLabel}</div></DropdownMenuTrigger><DropdownMenuContent className="w-56" align="start"><DropdownMenuGroup>{operatorValues.values.map(operator => <DropdownMenuItem key={operator.value} onClick={() => operatorValues.onOperatorChange?.(operator.value)}>{operator.label ?? tOperators(OPERATOR_LABEL_KEY[operator.value])}</DropdownMenuItem>)}</DropdownMenuGroup></DropdownMenuContent></DropdownMenu>);
    $[9] = activeOperator;
    $[10] = operatorLabel;
    $[11] = operatorValues;
    $[12] = tOperators;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const t5 = `[&_svg]:size-4 flex items-center gap-1 bg-card border-y border-l h-6 px-1.5 text-foreground font-medium text-xs cursor-pointer hover:bg-accent ${isCustom ? "" : "capitalize"}`;
  let t6;
  if ($[14] !== t5 || $[15] !== valueText) {
    t6 = <DropdownMenuTrigger asChild={true} data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="FilterIndicator.tsx"><div className={t5}>{valueText}</div></DropdownMenuTrigger>;
    $[14] = t5;
    $[15] = valueText;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const t7 = isCustom ? "p-0" : "w-56";
  let t8;
  if ($[17] !== isCustom) {
    t8 = isCustom ? {
      onCloseAutoFocus: _temp,
      onFocusOutside: _temp2
    } : {};
    $[17] = isCustom;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== activeValues || $[20] !== customContent || $[21] !== inactiveValues || $[22] !== isCustom || $[23] !== onChange) {
    t9 = isCustom ? isValidElement(customContent) ? cloneElement(customContent as React.ReactElement<any>, {
      onClose: () => setOpen(false)
    }) : customContent : <FilterMenuList activeValues={activeValues} inactiveValues={inactiveValues} onChange={onChange} />;
    $[19] = activeValues;
    $[20] = customContent;
    $[21] = inactiveValues;
    $[22] = isCustom;
    $[23] = onChange;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t7 || $[26] !== t8 || $[27] !== t9) {
    t10 = <DropdownMenuContent className={t7} align="start" {...t8} data-sentry-element="DropdownMenuContent" data-sentry-source-file="FilterIndicator.tsx">{t9}</DropdownMenuContent>;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== open || $[30] !== t10 || $[31] !== t6) {
    t11 = <DropdownMenu open={open} onOpenChange={setOpen} data-sentry-element="DropdownMenu" data-sentry-source-file="FilterIndicator.tsx">{t6}{t10}</DropdownMenu>;
    $[29] = open;
    $[30] = t10;
    $[31] = t6;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <X className="size-3 " data-sentry-element="X" data-sentry-source-file="FilterIndicator.tsx" />;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== onClear) {
    t13 = <div onClick={onClear} className="h-6 px-1.5 rounded-r bg-card border-y border-l border-r text-muted-foreground text-xs flex items-center hover:bg-accent cursor-pointer hover:text-foreground">{t12}</div>;
    $[34] = onClear;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== t11 || $[37] !== t13 || $[38] !== t3 || $[39] !== t4) {
    t14 = <div className="flex" data-sentry-component="FilterIndicator" data-sentry-source-file="FilterIndicator.tsx">{t3}{t4}{t11}{t13}</div>;
    $[36] = t11;
    $[37] = t13;
    $[38] = t3;
    $[39] = t4;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  return t14;
}
function _temp2(e_0) {
  return e_0.preventDefault();
}
function _temp(e) {
  return e.preventDefault();
}